import { Component, OnInit, Inject, inject } from "@angular/core";
import { StandardService } from "../services/standard.service";
import {
  StandardTypeEnum,
  UserRoleEnum,
  StandardListType,
  FilteringEnum,
  AllStandardsTabs,
  AddendumStatusEnum,
} from "../core/constant";
import { Router, ActivatedRoute } from "@angular/router";
import { CryptographyService } from "../services/cryptography.service";
import { LOCAL_STORAGE, WebStorageService } from "ngx-webstorage-service";
import { SelectedStandard } from "../models/selected-standard";
import { Attachment } from "../models/attachment";
import { FailModuleComponent } from "../fail-module/fail-module.component";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { DatePipe, Location } from "@angular/common";
import DOMPurify from "dompurify";

import { MatSnackBar } from "@angular/material/snack-bar";
import { SnackbarComponent } from "../snackbar/snackbar.component";

@Component({
  selector: "app-view-all-equipments-standard-detail-view",
  templateUrl: "./view-all-equipments-standard-detail-view.component.html",
  styleUrls: ["./view-all-equipments-standard-detail-view.component.scss"],
  providers: [DatePipe],
})
export class ViewAllEquipmentsStandardDetailViewComponent implements OnInit {
  private _snackBar = inject(MatSnackBar);
  detailsExpanded: boolean = false;
  allLocations: string = "";
  subLocations: string = "";
  grsId: number;
  loading: boolean = false;
  grsDetail: SelectedStandard = new SelectedStandard();
  userRole: number;
  notes: SafeHtml = "";
  additionalInformation: SafeHtml = "";
  get standardTypeEnum() {
    return StandardTypeEnum;
  }
  get userRoleEnum() {
    return UserRoleEnum;
  }
  get countryId(): number {
    const sessionCountryId = sessionStorage.getItem("selectedCountryID");
    if (sessionCountryId) {
      try {
        return typeof sessionCountryId === 'string' ? JSON.parse(sessionCountryId) : Number(sessionCountryId);
      } catch {
        return Number(sessionCountryId);
      }
    }
    const storageCountry = this.cryptographyService.decryptData(this.storage.get("countryId"));
    return Array.isArray(storageCountry) ? storageCountry[0] : Number(storageCountry);
  }
  get countryIds(): number[] {
    const cid = this.countryId;
    if (!cid) return [];
    return [Number(cid)];
  }

  get keyContactEmailResolved(): string {
    const d = this.grsDetail as any;
    return (
      d?.keyContact?.email ||
      d?.keyContact?.Email ||
      d?.standard?.keyContact?.email ||
      d?.standard?.keyContact?.Email ||
      d?.standard?.email ||
      d?.standard?.Email ||
      d?.email ||
      d?.Email ||
      (this as any).keyContactEmail ||
      ""
    );
  }

  get linksList(): any[] {
    const d = this.grsDetail as any;
    const rawLinks =
      d?.links ||
      d?.Links ||
      d?.standard?.links ||
      d?.standard?.Links ||
      d?.standard?.equipmentStandard?.links ||
      d?.standard?.equipmentStandard?.Links ||
      d?.standard?.equipmentStandard?.equipmentStandardLinks ||
      d?.standard?.equipmentStandard?.EquipmentStandardLinks ||
      d?.equipmentStandard?.links ||
      d?.equipmentStandard?.Links ||
      d?.equipmentStandard?.equipmentStandardLinks ||
      d?.equipmentStandard?.EquipmentStandardLinks ||
      d?.resourceLinks1 ||
      [];

    if (!Array.isArray(rawLinks)) return [];
    return rawLinks
      .map((l: any) => {
        const linkObj = l?.equipmentLink || l?.EquipmentLink || l?.link || l?.Link || l;
        const url = linkObj?.url || linkObj?.Url || "";
        const title = linkObj?.title || linkObj?.Title || linkObj?.name || linkObj?.Name || url;
        return { url, title };
      })
      .filter((l) => !!l.url?.trim() || !!l.title?.trim());
  }

  // Exceptions: an "active" exception is an Approved addendum whose renewal/
  // expiry date hasn't passed yet (day-level comparison, no expiry = active
  // indefinitely) - same rule as addendum.component.ts's isExceptionActive().
  private isAddendumActive(addendum: any): boolean {
    if (addendum?.addendumStatusId !== AddendumStatusEnum.Approved) return false;
    const renewalDate = addendum?.exceptionRenewalDate || addendum?.expiryDate;
    if (!renewalDate) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiryDay = new Date(renewalDate);
    expiryDay.setHours(0, 0, 0, 0);
    return today.getTime() <= expiryDay.getTime();
  }

  private get activeExceptions(): any[] {
    const addendum = (this.grsDetail as any)?.addendum || [];
    return addendum.filter((a: any) => this.isAddendumActive(a));
  }

  get hasActiveException(): boolean {
    return this.activeExceptions.length > 0;
  }

  private get ownCountryActiveException(): any {
    const cid = this.countryId;
    if (!cid) return null;
    return this.activeExceptions.find((a: any) => a.countryId === cid) || null;
  }

  private get exemptedCountryNames(): string {
    const names = this.activeExceptions
      .map((a: any) => a?.country?.name)
      .filter((n: string) => !!n);
    const unique = Array.from(new Set(names));
    if (unique.length === 0) return "";
    if (unique.length === 1) return unique[0];
    return `${unique.slice(0, -1).join(", ")} and ${unique[unique.length - 1]}`;
  }

  private formatExceptionPeriod(exception: any): string {
    const start = exception?.approvedOrRejectedDate
      ? this.datePipe.transform(exception.approvedOrRejectedDate, "d MMMM, yyyy")
      : null;
    const end = exception?.expiryDate
      ? this.datePipe.transform(exception.expiryDate, "d MMMM, yyyy")
      : null;
    if (start && end) return `${start} - ${end}`;
    if (end) return `Until ${end}`;
    return "";
  }

  // Multiple countries can each have their own exception dates - span the
  // earliest start to the latest end across all of them for the general
  // (non-market-specific) banner message.
  private formatExceptionPeriodRange(exceptions: any[]): string {
    const starts = exceptions
      .map((e: any) => (e?.approvedOrRejectedDate ? new Date(e.approvedOrRejectedDate) : null))
      .filter((d: Date | null) => !!d) as Date[];
    const ends = exceptions
      .map((e: any) => (e?.expiryDate ? new Date(e.expiryDate) : null))
      .filter((d: Date | null) => !!d) as Date[];
    const start = starts.length ? new Date(Math.min(...starts.map((d) => d.getTime()))) : null;
    const end = ends.length ? new Date(Math.max(...ends.map((d) => d.getTime()))) : null;
    const startStr = start ? this.datePipe.transform(start, "d MMMM, yyyy") : null;
    const endStr = end ? this.datePipe.transform(end, "d MMMM, yyyy") : null;
    if (startStr && endStr) return `${startStr} - ${endStr}`;
    if (endStr) return `Until ${endStr}`;
    return "";
  }

  // Global Admin always sees the general country-listing version, even if
  // their own assigned country happens to also be exempted - the "your
  // market" personalized wording is reserved for non-Global-Admin viewers
  // (Market Admin, Basic User, AOW Admin) tied to a single country.
  get showExemptionBanner(): boolean {
    if (this.userRole === UserRoleEnum.GlobalAdmin) {
      return this.hasActiveException;
    }
    return !!this.ownCountryActiveException;
  }

  get exemptionBannerTitle(): string {
    return this.userRole === UserRoleEnum.GlobalAdmin
      ? "This Equipment is Exempted"
      : "This Equipment is Exempted for Your Market";
  }

  get exemptionBannerMessage(): string {
    if (this.userRole === UserRoleEnum.GlobalAdmin) {
      const period = this.formatExceptionPeriodRange(this.activeExceptions);
      return `This Equipment is exempted for ${this.exemptedCountryNames}.${period ? " Exception Period: " + period + "." : ""}`;
    }
    const period = this.formatExceptionPeriod(this.ownCountryActiveException);
    return `You are not required to comply with this Equipment for your market.${period ? " Exception Period: " + period + "." : ""}`;
  }

  get hardwareDescription(): string {
    const d = this.grsDetail as any;
    return (
      d?.keyContact?.emailDescription ||
      d?.keyContact?.EmailDescription ||
      d?.standard?.keyContact?.emailDescription ||
      d?.standard?.keyContact?.EmailDescription ||
      d?.standard?.emailDescription ||
      d?.standard?.EmailDescription ||
      d?.emailDescription ||
      d?.EmailDescription ||
      d?.keyContactDesc ||
      d?.standard?.keyContactDesc ||
      ""
    );
  }

  constructor(
    private router: Router,
    private standardService: StandardService,
    private ngbModal: NgbModal,
    private cryptographyService: CryptographyService,
    @Inject(LOCAL_STORAGE)
    private storage: WebStorageService,
    private activeRoute: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private datePipe: DatePipe,
    private location: Location
  ) {
    this.grsId = parseInt(this.activeRoute.snapshot.paramMap.get("grsId"));
  }

  ngOnInit() {
    this.userRole = this.cryptographyService.decryptData(
      this.storage.get("userRole")
    );
    this.getDetailByGrbesId(this.grsId);
  }

  getDetailByGrbesId(grsId: number) {
    this.loading = true;
    //debugger;
    this.standardService
      .getGrbesDetailInfomationByStandardId(
        grsId,
        this.countryIds,
        this.standardService.equipmentStandardFiltration.filterSet,
        StandardListType.AllStandards,
        this.standardService.equipmentStandardFiltration.searchKey,
        this.getAppliedFilters()
      )
      .subscribe((data: SelectedStandard) => {
        void 0;
        this.grsDetail = new SelectedStandard(data);
        this.allLocations = this.grsDetail.standard.standardLocation.length > 0 ? this.grsDetail.standard.standardLocation.map((loc: any) => loc.locationType.name)
          .join(", ") : this.grsDetail.standard.locationType.name;
        this.subLocations = this.grsDetail.standard.standardLocation
          .map((loc: any) =>
            loc.locationType.subLocationTypes?.length
              ? loc.locationType.subLocationTypes.map((sloc) => sloc.name).join(", ")
              : "",
          )
          .filter(Boolean)
          .join(", ");
        void 0;

        // this.notes = this.sanitizer.bypassSecurityTrustHtml(
        //   this.grsDetail.standard.note
        // );
        const cleanNotes = DOMPurify.sanitize(this.grsDetail.standard.note);
        this.notes = this.sanitizer.bypassSecurityTrustHtml(cleanNotes);

        const cleanAddInfo = DOMPurify.sanitize(
          this.grsDetail.standard.additionalInformation
        );

        this.additionalInformation =
          this.sanitizer.bypassSecurityTrustHtml(cleanAddInfo);

        this.loading = false;
        () => {
          this.loading = false;
        };
      });
  }

  addToNrbes(standardId, standardTypeId) {
    this.loading = true;
    this.standardService
      .addStandardToNrbes(
        standardId,
        this.countryId,
        standardTypeId === 0 ? 6 : 3,
        this.cryptographyService.decryptData(this.storage.get("userId"))
      )
      .subscribe(() => {
        this.loading = false;
        this.getDetailByGrbesId(this.grsDetail.standard.id);
      });
  }

  navigateAccToType(type, id) {
    if (type == 4) {
      this.router.navigate(["/minimum-standard-detail-view/" + id]);
      return;
    }
    if (type == 8) {
      this.router.navigate(["/operations-standards-detail-view/" + id]);
      return;
    }
    if (type == 0) {
      this.router.navigate(["/view-equipments-standard-detail/" + id]);
      return;
    }
    if (type == 1 || type == 2) {
      this.router.navigate(["/global-recommendations-detail-view/" + id]);
      return;
    }
  }

  getAppliedFilters() {
    let idx =
      this.standardService.equipmentStandardFiltration.filterSet.findIndex(
        (i) => i.type == FilteringEnum.FilterByStandardType
      );
    if (idx == -1) return [];
    return this.standardService.equipmentStandardFiltration.filterSet[idx].ids;
  }
  slide(grsID: number, isForward: boolean) {
    this.loading = true;

    if (this.userRole != this.userRoleEnum.MarketAdmin) {
      this.standardService
        .getNextPreviousGrsInfomationByCurrentStandardId(
          grsID,
          isForward,
          this.countryIds,
          this.standardService.equipmentStandardFiltration.filterSet,
          StandardListType.AllStandards,
          this.standardService.equipmentStandardFiltration.searchKey,
          this.getAppliedFilters()
        )
        .subscribe((data: SelectedStandard) => {
          if (
            data.standard.standardTypeId !=
            this.standardTypeEnum.GlobalEquipmentStandard
          ) {
            this.router.navigate([
              "/view-equipments-standard-detail/" + data.standard.id,
            ]);
          }
          this.navigateAccToType(
            data.standard.standardTypeId,
            data.standard.id
          );
          this.grsDetail = new SelectedStandard(data);

          const cleanNotes = DOMPurify.sanitize(this.grsDetail.standard.note);
          this.notes = this.sanitizer.bypassSecurityTrustHtml(cleanNotes);

          const cleanAddInfo = DOMPurify.sanitize(
            this.grsDetail.standard.additionalInformation
          );

          this.additionalInformation =
            this.sanitizer.bypassSecurityTrustHtml(cleanAddInfo);

          this.loading = false;
          () => {
            this.loading = false;
          };
        });
    } else {
      this.standardService
        .getNextPreviousGrsInfomationByCurrentStandardId(
          grsID,
          isForward,
          this.countryIds,
          this.standardService.equipmentStandardFiltration.filterSet,
          StandardListType.AllStandards,
          this.standardService.equipmentStandardFiltration.searchKey,
          this.getAppliedFilters()
        )
        .subscribe((data: SelectedStandard) => {
          if (
            data.standard.standardTypeId !=
            this.standardTypeEnum.GlobalEquipmentStandard
          ) {
            this.router.navigate([
              "/view-equipments-standard-detail/" + data.standard.id,
            ]);
          }
          this.navigateAccToType(
            data.standard.standardTypeId,
            data.standard.id
          );
          this.grsDetail = new SelectedStandard(data);

          const cleanNotes = DOMPurify.sanitize(this.grsDetail.standard.note);
          this.notes = this.sanitizer.bypassSecurityTrustHtml(cleanNotes);

          const cleanAddInfo = DOMPurify.sanitize(
            this.grsDetail.standard.additionalInformation
          );

          this.additionalInformation =
            this.sanitizer.bypassSecurityTrustHtml(cleanAddInfo);

          this.loading = false;
          () => {
            this.loading = false;
          };
        });
    }
  }

  onDownloadSelectedEquipmentAttachment(e) {
    //debugger;
    return this.standardService.getAttachmentUrl(e.id, false).subscribe(
      (response: any) => {
        const blob = new Blob([response], {
          type: response.type,
        });
        if (
          (window.navigator as any) &&
          (window.navigator as any).msSaveOrOpenBlob
        ) {
          (window.navigator as any).msSaveOrOpenBlob(blob, e.fileName);
          return;
        }
        var a = document.createElement("a");
        document.body.appendChild(a);

        let url = window.URL.createObjectURL(blob);

        a.href = url;
        a.download = e.fileName;
        a.click();
        window.URL.revokeObjectURL(url);

        //window.open(url);
      },
      (error: any) => {
        const modalRef = this.ngbModal.open(FailModuleComponent);
        modalRef.componentInstance.errorMessage = error.error
          ? error.error.Message
          : error.message;
      }
    );
  }

  downloadExcel() {
    this.standardService.downloadSingleStandardExcel(
      this.grsDetail,
      "equipment"
    );
    return;
  }

  isShareModalOpen = false;
  shareModalTitle = "";
  shareModalSubtitle = "";
  shareModalUrl = "";

  openShareModal(): void {
    const standard = (this.grsDetail as any)?.standard;
    this.shareModalTitle = standard?.title || "";
    this.shareModalSubtitle = [
      standard?.standardType?.name,
      standard?.category?.name,
    ]
      .filter(Boolean)
      .join(" · ");
    this.shareModalUrl = window.location.href;
    this.isShareModalOpen = true;
  }
  getLocationNames(item): any {
    if (
      item.standard.standardLocation &&
      item.standard.standardLocation.length > 0
    ) {
      return item.standard.standardLocation
        .map((loc) => loc.locationType.name)
        .join(", ");
    } else {
      return item.standard.locationType.name;
    }
  }
  toggleDetails() {
    this.detailsExpanded = !this.detailsExpanded;
  }

  getDetails() {
    return this.sanitizer.bypassSecurityTrustHtml(
      this.grsDetail.standard.note.replace(/\n/g, "<br>")
    );
  }

  openSnackBar() {
    this._snackBar.openFromComponent(SnackbarComponent, {
      duration: 2000,
      data: {
        message: "Object Copied to clipboard!",
      },
    });
  }
  copyText(content: any) {
    void 0;
    navigator.clipboard.writeText(content);
    this.openSnackBar();
  }

  goBack() {
    if (window.history.length > 1) {
      this.location.back();
    } else {
      this.router.navigate(["/view-all-equipments"]);
    }
  }
}
